import { Tooltip } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { LibraryManager } from '@stores/LibraryManager/LibraryManager';
import {
  BeginEndTimestamp,
  Clip,
  ExtendedMediaId,
  useMomentMergeStore,
} from '@stores/MomentMerge/MomentMergeStore';
import {
  decodeMomentFromString,
  encodeMomentToString,
  MomentIdentifierString,
} from '@stores/MomentSelection/MomentSelection';
import { trackingStore } from '@stores/Tracking';
import { observer } from 'mobx-react';
import { useRef } from 'react';
import { MomentMergeModal, MomentMergeModalRef } from '../MomentMerge/MomentMergeModal';

interface MomentMergeButtonProps {
  libraryManager: LibraryManager;
}

export const MomentMergeButton = observer(function MomentMergeButton({
  libraryManager,
}: MomentMergeButtonProps) {
  const momentMergeModalRef = useRef<MomentMergeModalRef>(null);
  const momentMergeStore = useMomentMergeStore();

  const librarySearch = libraryManager.multiLibSearch;
  const momentSelection = librarySearch.momentSelection;

  const enoughMomentsSelected = (() => {
    return momentSelection.selectedMoments.size >= 2;
  })();

  // Check if we should be able to open the merge modal
  const canOpenMergeModal = (() => {
    const selectedMoments = Array.from(momentSelection.selectedMoments.values())
      .map((momentId) => {
        return decodeMomentFromString(momentId as MomentIdentifierString);
      })
      .filter((moment) => moment !== null);

    // Need at least 2 moments to merge
    if (selectedMoments.length < 2) {
      return false;
    }

    // Check if any moment has a duration of 1 millisecond or less
    for (const moment of selectedMoments) {
      const duration = moment.endTime - moment.startTime;
      if (duration <= 0.001) {
        return false;
      }
    }

    return true;
  })();

  const handleOpenMomentMergeModal = () => {
    trackingStore.momentMergeButtonClicked({ momentCount: momentSelection.selectedMoments.size });
    const selectedMoments = Array.from(momentSelection.selectedMoments)
      .map((momentId) => {
        const result = decodeMomentFromString(momentId as MomentIdentifierString);
        if (!result) return null;
        return result;
      })
      .filter((result) => result !== null);

    if (selectedMoments.length > 1) {
      momentMergeStore.setClips(
        selectedMoments.map((result) => {
          // Create a new clip with the moment's metadata
          return new Clip(
            encodeMomentToString(
              result.libraryId,
              result.mediaId,
              result.startTime,
              result.endTime,
            ),
            new ExtendedMediaId(result.mediaId, result.libraryId),
            new BeginEndTimestamp(result.startTime, result.endTime),
          );
        }),
      );
      momentMergeModalRef.current?.open();
    }
  };

  // Don't render if not enough moments selected
  if (!enoughMomentsSelected) {
    return null;
  }

  return (
    <>
      {/* This contains the MomentMergeModal and the button which opens it. */}
      <MomentMergeModal ref={momentMergeModalRef} />
      <Tooltip title={!canOpenMergeModal ? 'Merging with images is not implemented yet.' : ''}>
        <span>
          <ActionButton
            variant="primary"
            disabled={!canOpenMergeModal}
            onClick={handleOpenMomentMergeModal}
          >
            Merge moments
          </ActionButton>
        </span>
      </Tooltip>
    </>
  );
});
